// @flow
import _ from 'lodash';
import moment from 'moment';

import {
  SI_GET_BOOST_TEMPLATES_REQUEST,
  SI_GET_BOOST_TEMPLATES_SUCCESS,
  SI_GET_BOOST_TEMPLATES_FAILURE,
  SI_GET_TEMPLATE_INSTANCES_REQUEST,
  SI_GET_TEMPLATE_INSTANCES_SUCCESS,
  SI_GET_TEMPLATE_INSTANCES_FAILURE,
  SI_ADD_CREATED_TEMPLATE_INSTANCE,
  SI_CLONE_BOOST_TEMPLATES_REQUEST,
  SI_CLONE_BOOST_TEMPLATES_SUCCESS,
  SI_CLONE_BOOST_TEMPLATES_FAILURE,
  SI_RENAME_TEMPLATES_REQUEST,
  SI_RENAME_TEMPLATES_SUCCESS,
  SI_RENAME_TEMPLATES_FAILURE,
  SI_REMOVE_TEMPLATE_REQUEST,
  SI_REMOVE_TEMPLATE_SUCCESS,
  SI_REMOVE_TEMPLATE_FAILURE,
  SI_DUPLICATE_TEMPLATE_REQUEST,
  SI_DUPLICATE_TEMPLATE_SUCCESS,
  SI_DUPLICATE_TEMPLATE_FAILURE,
  SITemplateInstanceState,
  SITemplateInstanceAction,
  SI_GET_TEMPLATE_INSTANCE_REQUEST,
  SI_GET_TEMPLATE_INSTANCE_SUCCESS,
  SI_GET_TEMPLATE_INSTANCE_FAILURE,
  UPDATE_WIDGET_INSTANCE_TEMPLATE_REQUEST,
  UPDATE_WIDGET_INSTANCE_TEMPLATE_SUCCESS,
  UPDATE_WIDGET_INSTANCE_TEMPLATE_FAILURE,
  SI_CLEAN_STATE_EXIT_MANAGE_TEMPLATE_PAGE,
  UPDATE_FIELD,
  UPDATE_MULTI_FIELDS,
  SI_UPDATE_THEME_IDS_OF_TEMPLATE,
  SI_GET_WIDGET_VERSIONS_REQUEST,
  SI_GET_WIDGET_VERSIONS_SUCCESS,
  SI_GET_WIDGET_VERSIONS_FAILURE,
  SI_ADD_UPGRADED_TEMPLATE_REQUEST,
  SI_ADD_UPGRADED_TEMPLATE_SUCCESS,
  SI_ADD_UPGRADED_TEMPLATE_FAILURE,
  SI_IMPORT_TEMPLATE_REQUEST,
  SI_IMPORT_TEMPLATE_SUCCESS,
  SI_IMPORT_TEMPLATE_FAILURE
} from 'stores/flow-type/shopify-integration/template-instance.type';

const initialState: SITemplateInstanceState = {
  getBoostTemplatesLoading: false,
  getBoostTemplatesSuccess: false,
  boostTemplates: [],
  widgetVersion: {},
  widgetVersionLoading: false,
  widgetVersionSuccess: false,

  getTemplateInstancesLoading: false,
  getTemplateInstancesSuccess: false,
  templateInstances: [],
  templateInstancesPreview: [],
  sortedTemplateInstances: [],
  templateUpgradeableCheck: {},
  templateInstance: {},

  cloneBoostTemplatesLoading: false,
  cloneBoostTemplatesSuccess: false,
  clonedTemplateInstance: {},

  renameTemplatesLoading: false,
  renameTemplatesSuccess: false,
  renamedTemplateInstance: {},

  removeTemplateLoading: false,
  removeTemplateSuccess: false,

  duplicateTemplateLoading: false,
  duplicateTemplateSuccess: false,
  duplicatedTemplateInstance: {},

  addedUpgradedTemplate: null,
  latestAddedUpgradedTemplateTemporaryId: '',
  addUpgradedTemplateLoading: false,
  addUpgradedTemplateSuccess: false,
  latestAddedUpgradedTemplateId: '',

  importTemplateLoading: false,
  importTemplateSuccess: false,
  importTemplate: {}
};

const TemplateInstanceReducers = (
  state: SITemplateInstanceState = initialState,
  action: SITemplateInstanceAction
) => {
  switch (action.type) {
    case UPDATE_FIELD: {
      const { fieldName, data } = action?.payload ?? {};
      return {
        ...state,
        [fieldName]: data
      };
    }

    case UPDATE_MULTI_FIELDS: {
      const { data } = action?.payload ?? {};
      return {
        ...state,
        ...data
      };
    }

    // Boost templates
    case SI_GET_BOOST_TEMPLATES_REQUEST:
      return {
        ...state,
        getBoostTemplatesLoading: true,
        getBoostTemplatesSuccess: false,
        boostTemplates: []
      };
    case SI_GET_BOOST_TEMPLATES_SUCCESS: {
      const { data = {} } = action.payload;
      const publishedDefaultTemplates = _.get(data, 'publishedDefaultTemplates', []);
      const internalReleaseDefaultTemplates = _.get(data, 'internalReleaseDefaultTemplates', []);

      return {
        ...state,
        getBoostTemplatesLoading: false,
        getBoostTemplatesSuccess: true,
        error: undefined,
        boostTemplates: publishedDefaultTemplates,
        internalReleaseBoostTemplates: internalReleaseDefaultTemplates
      };
    }
    case SI_GET_BOOST_TEMPLATES_FAILURE:
      return {
        ...state,
        getBoostTemplatesLoading: false,
        getBoostTemplatesSuccess: false,
        boostTemplates: [],
        error: action.payload
      };

    // Template instances
    case SI_GET_TEMPLATE_INSTANCES_REQUEST:
      return {
        ...state,
        getTemplateInstancesLoading: true,
        getTemplateInstancesSuccess: false,
        templateInstances: []
      };
    case SI_GET_TEMPLATE_INSTANCES_SUCCESS: {
      const { data = {} } = action.payload;

      return {
        ...state,
        getTemplateInstancesLoading: false,
        getTemplateInstancesSuccess: true,
        error: undefined,
        templateInstances: _.get(data, 'templateInstances', []),
        latestAddedUpgradedTemplateId: _.get(data, 'latestAddedUpgradedTemplate', '')
      };
    }
    case SI_GET_TEMPLATE_INSTANCES_FAILURE:
      return {
        ...state,
        getTemplateInstancesLoading: false,
        getTemplateInstancesSuccess: false,
        templateInstances: [],
        error: action.payload
      };

    // Add created template instance
    case SI_ADD_CREATED_TEMPLATE_INSTANCE: {
      const templateInstances = [...(state.templateInstances || [])];

      // Update manual values
      const { data: templateInstance, isBoostTemplate } = action.payload;
      if (!_.isEmpty(templateInstance)) {
        if (isBoostTemplate) {
          // Push at first position of array
          templateInstances.unshift(templateInstance);
        } else {
          // Replace old template instance
          const index = _.findIndex(
            templateInstances,
            instance => instance.templateId === templateInstance.templateId
          );
          if (index !== -1) {
            templateInstances[index] = templateInstance;
          }
        }
      }

      return {
        ...state,
        templateInstances
      };
    }

    // Template instance
    case SI_GET_TEMPLATE_INSTANCE_REQUEST:
      return {
        ...state,
        getTemplateInstancesLoading: true,
        getTemplateInstancesSuccess: false,
        templateInstance: []
      };
    case SI_GET_TEMPLATE_INSTANCE_SUCCESS: {
      const { data = [] } = action.payload;
      const instantWidget = _.cloneDeep(_.get(data, 'widgets', {}));

      Object.keys(instantWidget).forEach(key => {
        instantWidget[key] = instantWidget[key].map(item => {
          if (_.isEmpty(item.customizeTemplate)) {
            const customizeTemplate = item.widgetTemplate;
            return {
              ...item,
              customizeTemplate
            };
          }
          return item;
        });
      });

      return {
        ...state,
        getTemplateInstancesLoading: false,
        getTemplateInstancesSuccess: true,
        error: undefined,
        templateInstance: { ...data, widgets: instantWidget },
        templateInstancesPreview: { ...data, widgets: instantWidget }
      };
    }
    case SI_GET_TEMPLATE_INSTANCE_FAILURE:
      return {
        ...state,
        getTemplateInstancesLoading: false,
        getTemplateInstancesSuccess: false,
        templateInstance: {},
        error: action.payload
      };

    // Update Template instance widgets
    case UPDATE_WIDGET_INSTANCE_TEMPLATE_REQUEST:
      return {
        ...state,
        updateTemplateInstancesLoading: true,
        updateTemplateInstancesSuccess: false,
        updateTemplateInstancesError: undefined
      };
    case UPDATE_WIDGET_INSTANCE_TEMPLATE_SUCCESS: {
      const { editedWidget } = action.payload;
      if (action.error) {
        return {
          ...state,
          updateTemplateInstancesLoading: false,
          updateTemplateInstancesSuccess: false,
          error: action.payload,
          updateTemplateInstancesError: action.payload
        };
      }

      // Only update edited widget and keeping other original widgets for redux form compares
      const newTemplateInstance = _.cloneDeep(state.templateInstance);
      const widgetKeys = Object.keys(newTemplateInstance.widgets);
      for (const key of widgetKeys) {
        const widgets = newTemplateInstance.widgets[key];
        for (let i = 0; i < widgets.length; i++) {
          const widget = widgets[i];
          if (widget?.widgetId === editedWidget?.widgetId) {
            widgets[i] = editedWidget; // Update here
            break;
          }
        }
      }

      return {
        ...state,
        templateInstance: newTemplateInstance,
        updateTemplateInstancesLoading: false,
        updateTemplateInstancesSuccess: true,
        error: undefined,
        updateTemplateInstancesError: undefined
      };
    }
    case UPDATE_WIDGET_INSTANCE_TEMPLATE_FAILURE:
      return {
        ...state,
        templateInstance: state.templateInstance,
        updateTemplateInstancesLoading: false,
        updateTemplateInstancesSuccess: false,
        error: action.payload,
        updateTemplateInstancesError: action.payload
      };

    // Clone Boost templates
    case SI_CLONE_BOOST_TEMPLATES_REQUEST:
      return {
        ...state,
        cloneBoostTemplatesLoading: true,
        cloneBoostTemplatesSuccess: false,
        clonedTemplateInstance: {}
      };
    case SI_CLONE_BOOST_TEMPLATES_SUCCESS: {
      const { data = {} } = action.payload;
      return {
        ...state,
        cloneBoostTemplatesLoading: false,
        cloneBoostTemplatesSuccess: true,
        error: undefined,
        clonedTemplateInstance: data,
        duplicatedTemplateInstance: {}, // Reset state
        importTemplate: {} // Reset state
      };
    }
    case SI_CLONE_BOOST_TEMPLATES_FAILURE:
      return {
        ...state,
        cloneBoostTemplatesLoading: false,
        cloneBoostTemplatesSuccess: false,
        clonedTemplateInstance: {},
        error: action.payload
      };

    // Rename template instance
    case SI_RENAME_TEMPLATES_REQUEST:
      return {
        ...state,
        renameTemplatesLoading: true,
        renameTemplatesSuccess: false,
        renamedTemplateInstance: {}
      };
    case SI_RENAME_TEMPLATES_SUCCESS: {
      const { data = {} } = action.payload;
      const { templateInstances = [] } = state;

      // Rename selected template in state
      const index = _.findIndex(state.templateInstances, templateInstance => {
        return templateInstance.templateId === data.templateId;
      });

      if (index !== -1) {
        templateInstances[index] = data;
      }

      return {
        ...state,
        renameTemplatesLoading: false,
        renameTemplatesSuccess: true,
        error: undefined,
        templateInstances,
        renamedTemplateInstance: data
      };
    }
    case SI_RENAME_TEMPLATES_FAILURE:
      return {
        ...state,
        renameTemplatesLoading: false,
        renameTemplatesSuccess: false,
        renamedTemplateInstance: {},
        error: action.payload
      };

    // Remove template instance
    case SI_REMOVE_TEMPLATE_REQUEST:
      return {
        ...state,
        removeTemplateLoading: true,
        removeTemplateSuccess: false
      };
    case SI_REMOVE_TEMPLATE_SUCCESS: {
      const { templateId } = action.payload;
      const { templateInstances = [] } = state;

      return {
        ...state,
        removeTemplateLoading: false,
        removeTemplateSuccess: true,
        error: undefined,
        templateInstances: templateInstances.filter(template => template.templateId !== templateId)
      };
    }
    case SI_REMOVE_TEMPLATE_FAILURE:
      return {
        ...state,
        removeTemplateLoading: false,
        removeTemplateSuccess: false,
        error: action.payload
      };

    // Duplicate template instance
    case SI_DUPLICATE_TEMPLATE_REQUEST:
      return {
        ...state,
        duplicateTemplateLoading: true,
        duplicateTemplateSuccess: false,
        duplicatedTemplateInstance: {}
      };
    case SI_DUPLICATE_TEMPLATE_SUCCESS: {
      const { data = {} } = action.payload;
      const { templateInstances = [] } = state;

      // Update manual values
      const duplicatedTemplateInstance = data;

      if (!_.isEmpty(duplicatedTemplateInstance)) {
        // Push at first position of array
        templateInstances.unshift(duplicatedTemplateInstance);
      }

      return {
        ...state,
        duplicateTemplateLoading: false,
        duplicateTemplateSuccess: true,
        error: undefined,
        templateInstances,
        duplicatedTemplateInstance: duplicatedTemplateInstance,
        clonedTemplateInstance: {}, // Reset state
        importTemplate: {} // Reset state
      };
    }
    case SI_DUPLICATE_TEMPLATE_FAILURE:
      return {
        ...state,
        duplicateTemplateLoading: false,
        duplicateTemplateSuccess: false,
        duplicatedTemplateInstance: {},
        error: action.payload
      };

    // Add created template instance
    case SI_CLEAN_STATE_EXIT_MANAGE_TEMPLATE_PAGE:
      return {
        ...state,
        duplicatedTemplateInstance: {},
        clonedTemplateInstance: {},
        importTemplate: {}
      };

    case SI_UPDATE_THEME_IDS_OF_TEMPLATE: {
      const { fromTemplateId, toTemplateId, themeId } = action?.payload || {};
      const templateInstances = _.cloneDeep(state.templateInstances) || [];

      const fromTemplate = _.find(templateInstances, { templateId: fromTemplateId });
      fromTemplate.themeIds = fromTemplate.themeIds.filter(id => id !== themeId);

      const toTemplate = _.find(templateInstances, { templateId: toTemplateId });
      toTemplate.themeIds.push(themeId);
      return {
        ...state,
        templateInstances
      };
    }

    case SI_GET_WIDGET_VERSIONS_REQUEST:
      return {
        ...state,
        widgetVersionLoading: true,
        widgetVersionSuccess: false
      };
    case SI_GET_WIDGET_VERSIONS_SUCCESS: {
      const { data = [], widgetId } = action.payload;
      const sortedData = [...data].sort((a, b) =>
        moment(a.lastModified).isAfter(moment(b.lastModified)) ? -1 : 1
      );

      const widgetVersion = _.cloneDeep(state.widgetVersion);
      _.set(widgetVersion, widgetId, {
        selectedVersionId: sortedData[0]?.versionId || '',
        versions: sortedData
      });

      return {
        ...state,
        widgetVersion,
        widgetVersionLoading: false,
        widgetVersionSuccess: true,
        error: undefined
      };
    }
    case SI_GET_WIDGET_VERSIONS_FAILURE:
      return {
        ...state,
        widgetVersionLoading: false,
        widgetVersionSuccess: false,
        error: action.payload
      };

    case SI_ADD_UPGRADED_TEMPLATE_REQUEST: {
      const { templateInstance, temporaryId } = action.payload;
      if (temporaryId) {
        const skeletonTemplate = {
          templateName: `Updated ${templateInstance.templateName}`,
          isUpgrading: true,
          temporaryId
        };
        const clonedTemplateInstances = _.cloneDeep(state.templateInstances) || [];
        clonedTemplateInstances.unshift(skeletonTemplate);

        return {
          ...state,
          addUpgradedTemplateLoading: true,
          addUpgradedTemplateSuccess: false,
          latestAddedUpgradedTemplateTemporaryId: temporaryId,
          templateInstances: clonedTemplateInstances
        };
      }

      return {
        ...state,
        addUpgradedTemplateLoading: true,
        addUpgradedTemplateSuccess: false
      };
    }
    case SI_ADD_UPGRADED_TEMPLATE_SUCCESS: {
      const { data, addedIndex } = action.payload;
      const { templateInstances } = state;

      if (!_.isNil(addedIndex)) {
        const clonedTemplateInstances = _.cloneDeep(templateInstances);
        clonedTemplateInstances.splice(addedIndex, 0, data);
        return {
          ...state,
          addedUpgradedTemplate: data,
          addUpgradedTemplateLoading: false,
          addUpgradedTemplateSuccess: true,
          templateInstances: clonedTemplateInstances,
          error: undefined
        };
      }

      if (!_.isEmpty(data)) {
        const { temporaryId } = data;
        const clonedTemplateInstances = _.cloneDeep(templateInstances);
        const skeletonIndex = clonedTemplateInstances.findIndex(
          template => template.temporaryId === temporaryId
        );
        if (skeletonIndex !== -1) {
          clonedTemplateInstances[skeletonIndex] = data;
          return {
            ...state,
            addedUpgradedTemplate: data,
            addUpgradedTemplateLoading: false,
            addUpgradedTemplateSuccess: true,
            templateInstances: clonedTemplateInstances,
            error: undefined
          };
        }
      }
      return state;
    }
    case SI_ADD_UPGRADED_TEMPLATE_FAILURE:
      return {
        ...state,
        addedUpgradedTemplate: null,
        addUpgradedTemplateLoading: false,
        addUpgradedTemplateSuccess: false,
        error: action.payload
      };

    case SI_IMPORT_TEMPLATE_REQUEST:
      return {
        ...state,
        importTemplateLoading: true,
        importTemplateSuccess: false,
      };
    case SI_IMPORT_TEMPLATE_SUCCESS: {
      const data = action.payload;
      console.log('Import template instance success with data: ', data);
      const templateInstances = _.cloneDeep(state.templateInstances) || [];
      templateInstances.unshift(data);
      return {
        ...state,
        importTemplateLoading: false,
        importTemplateSuccess: true,
        error: undefined,
        templateInstances,
        importTemplate: data,
        duplicatedTemplateInstance: {}, // Reset state
        clonedTemplateInstance: {} // Reset state
      };
    }
    case SI_IMPORT_TEMPLATE_FAILURE:
      return {
        ...state,
        importTemplateLoading: false,
        importTemplateSuccess: false,
        error: action.payload
      };
    default:
      return state;
  }
};
export default TemplateInstanceReducers;
